import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { fetchMyReflection, saveReflection } from "@/lib/reflections";
import { analytics } from "@/lib/analytics";

const MOODS = [
  { v: 1, label: "Drained", emoji: "😞" },
  { v: 2, label: "Meh", emoji: "😕" },
  { v: 3, label: "Steady", emoji: "🙂" },
  { v: 4, label: "Energised", emoji: "😄" },
  { v: 5, label: "On fire", emoji: "🔥" },
];

const STEPS = ["mood", "wins", "learnings", "interests", "goals", "challenges", "review"] as const;
type StepKey = typeof STEPS[number];

export default function Reflect() {
  const { monthIndex: param } = useParams();
  const navigate = useNavigate();
  const monthIndex = Math.min(12, Math.max(1, parseInt(param ?? "1", 10) || 1));

  const [stepIdx, setStepIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [mood, setMood] = useState<number | null>(null);
  const [wins, setWins] = useState<string[]>(["", "", ""]);
  const [learnings, setLearnings] = useState("");
  const [interests, setInterests] = useState<string[]>([]);
  const [interestDraft, setInterestDraft] = useState("");
  const [goals, setGoals] = useState("");
  const [challenges, setChallenges] = useState("");

  useEffect(() => {
    analytics.reflectView({ month_index: monthIndex });
    fetchMyReflection(monthIndex).then((r) => {
      if (r) {
        setMood(r.mood);
        setWins([...(r.wins ?? []), "", "", ""].slice(0, 3));
        setLearnings(r.learnings ?? "");
        setInterests(r.interests ?? []);
        setGoals(r.goals_next_month ?? "");
        setChallenges(r.challenges ?? "");
      }
      setLoading(false);
    });
  }, [monthIndex]);

  // Track every step view (incl. first render and back/next).
  useEffect(() => {
    analytics.reflectStepView({
      month_index: monthIndex,
      step_index: stepIdx,
      step: STEPS[stepIdx],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx, monthIndex]);

  const step: StepKey = STEPS[stepIdx];
  const progress = ((stepIdx + 1) / STEPS.length) * 100;

  function addInterest() {
    const v = interestDraft.trim();
    if (!v) return;
    if (interests.includes(v)) { setInterestDraft(""); return; }
    setInterests([...interests, v]);
    setInterestDraft("");
  }

  async function handleSubmit() {
    setSaving(true);
    analytics.reflectSubmitAttempt({ month_index: monthIndex, mood, wins_count: wins.filter((w) => w.trim()).length, interests_count: interests.length });
    try {
      await saveReflection({
        month_index: monthIndex,
        wins: wins.map((w) => w.trim()).filter(Boolean),
        learnings: learnings.trim() || null,
        interests,
        goals_next_month: goals.trim() || null,
        challenges: challenges.trim() || null,
        mood,
      });
      analytics.reflectSubmitSuccess({ month_index: monthIndex });
      toast.success("Reflection saved · +75 XP");
      navigate("/journey");
    } catch (e: any) {
      analytics.reflectSubmitError({ month_index: monthIndex, message: e?.message ?? "unknown" });
      toast.error(e?.message ?? "Could not save reflection");
    } finally {
      setSaving(false);
    }
  }

  const canNext = (() => {
    if (step === "mood") return mood !== null;
    if (step === "wins") return wins.some((w) => w.trim().length > 0);
    if (step === "learnings") return learnings.trim().length > 0;
    if (step === "interests") return interests.length > 0;
    if (step === "goals") return goals.trim().length > 0;
    if (step === "challenges") return true;
    return true;
  })();

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/85 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Button asChild variant="ghost" size="sm" className="gap-1.5 -ml-2">
            <Link to="/journey"><ArrowLeft className="h-4 w-4" /> Journey</Link>
          </Button>
          <Badge variant="outline" className="gap-1.5">
            <Sparkles className="h-3 w-3 text-primary" /> Month {monthIndex} reflection
          </Badge>
        </div>
        <div className="h-1 bg-muted">
          <motion.div
            className="h-full bg-primary"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {loading ? (
          <Card><CardContent className="p-10 text-center text-muted-foreground">Loading…</CardContent></Card>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
            >
              {step === "mood" && (
                <StepShell title="How did this month feel?" hint="One tap. Trust your gut.">
                  <div className="grid grid-cols-5 gap-2 sm:gap-3">
                    {MOODS.map((m) => (
                      <button
                        key={m.v}
                        onClick={() => setMood(m.v)}
                        className={`rounded-2xl border-2 p-3 sm:p-4 flex flex-col items-center gap-1 transition ${
                          mood === m.v ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"
                        }`}
                      >
                        <span className="text-3xl">{m.emoji}</span>
                        <span className="text-[11px] sm:text-xs text-muted-foreground">{m.label}</span>
                      </button>
                    ))}
                  </div>
                </StepShell>
              )}

              {step === "wins" && (
                <StepShell title="What are 3 wins from this month?" hint="Small counts. A finished assignment, a kind word, a habit kept.">
                  <div className="space-y-3">
                    {wins.map((w, i) => (
                      <Input
                        key={i}
                        value={w}
                        onChange={(e) => {
                          const next = [...wins]; next[i] = e.target.value; setWins(next);
                        }}
                        placeholder={`Win #${i + 1}`}
                        className="h-12 text-base"
                      />
                    ))}
                  </div>
                </StepShell>
              )}

              {step === "learnings" && (
                <StepShell title="What did you learn about yourself?" hint="A single sentence is enough.">
                  <Textarea
                    value={learnings}
                    onChange={(e) => setLearnings(e.target.value)}
                    placeholder="I noticed that I…"
                    className="min-h-32 text-base"
                  />
                </StepShell>
              )}

              {step === "interests" && (
                <StepShell title="What pulled at your curiosity?" hint="Topics, fields, ideas you kept coming back to. Add as many as you like.">
                  <div className="flex gap-2">
                    <Input
                      value={interestDraft}
                      onChange={(e) => setInterestDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addInterest(); } }}
                      placeholder="e.g. climate tech, design, neuroscience…"
                      className="h-12 text-base"
                    />
                    <Button onClick={addInterest} type="button" size="lg">Add</Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {interests.map((t) => (
                      <Badge key={t} variant="secondary" className="gap-1.5 pl-3 pr-1 py-1.5">
                        {t}
                        <button
                          onClick={() => setInterests(interests.filter((x) => x !== t))}
                          className="rounded-full hover:bg-muted p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </StepShell>
              )}

              {step === "goals" && (
                <StepShell title="What's your focus for next month?" hint="One clear intention beats five vague ones.">
                  <Textarea
                    value={goals}
                    onChange={(e) => setGoals(e.target.value)}
                    placeholder="Next month I will…"
                    className="min-h-32 text-base"
                  />
                </StepShell>
              )}

              {step === "challenges" && (
                <StepShell title="Anything blocking you?" hint="Optional. Naming a challenge is the first step to clearing it.">
                  <Textarea
                    value={challenges}
                    onChange={(e) => setChallenges(e.target.value)}
                    placeholder="The thing slowing me down is…"
                    className="min-h-32 text-base"
                  />
                </StepShell>
              )}

              {step === "review" && (
                <StepShell title="Ready to save?" hint="You can edit this reflection anytime this month.">
                  <div className="space-y-3 text-sm">
                    <ReviewLine label="Mood" value={mood ? `${MOODS.find((m) => m.v === mood)?.emoji} ${MOODS.find((m) => m.v === mood)?.label}` : "—"} />
                    <ReviewLine label="Wins" value={wins.filter(Boolean).join(" · ") || "—"} />
                    <ReviewLine label="Learning" value={learnings || "—"} />
                    <ReviewLine label="Interests" value={interests.join(", ") || "—"} />
                    <ReviewLine label="Next month" value={goals || "—"} />
                    <ReviewLine label="Challenges" value={challenges || "—"} />
                  </div>
                </StepShell>
              )}
            </motion.div>
          </AnimatePresence>
        )}

        <div className="flex items-center justify-between mt-8">
          <Button
            variant="ghost"
            disabled={stepIdx === 0}
            onClick={() => {
              analytics.reflectStepBack({ month_index: monthIndex, from_step: STEPS[stepIdx] });
              setStepIdx((i) => Math.max(0, i - 1));
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {step !== "review" ? (
            <Button disabled={!canNext} onClick={() => {
              analytics.reflectStepAdvance({ month_index: monthIndex, from_step: STEPS[stepIdx], to_step: STEPS[Math.min(STEPS.length - 1, stepIdx + 1)] });
              setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
            }}>
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button disabled={saving} onClick={handleSubmit}>
              <Check className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save reflection"}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

function StepShell({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <Card className="border-border/60">
      <CardContent className="p-6 sm:p-10 space-y-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">{hint}</p>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function ReviewLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3 py-2 border-b border-border/40 last:border-0">
      <span className="w-28 shrink-0 text-muted-foreground uppercase tracking-wider text-[11px] font-semibold">{label}</span>
      <span className="flex-1 text-foreground">{value}</span>
    </div>
  );
}
